/**
 * Создание и правка анонса.
 *
 * Оба языка правятся сразу: анонс читают на своём, и пустая вторая версия
 * означала бы пустое окно у половины людей. Сервер, впрочем, обязательным
 * считает только один — второй он заполнит первым.
 */
import { useState } from "react";
import { Create, Edit, useForm } from "@refinedev/antd";
import { Alert, Button, Form, Input, Space, Tag, Typography, message } from "antd";

import { ImageUpload } from "../../components/ImageUpload";
import { request } from "../../lib/http";
import type { Announcement } from "./list";

function Fields({ coverUrl }: { coverUrl?: string }) {
  return (
    <>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Опубликованный анонс всплывает у каждого пользователя один раз"
        description="Пока анонс не опубликован, его не видит никто. Публикация — отдельная кнопка: она показывает анонс всем, включая тех, кто уже вошёл, и второй раз он никому не всплывёт."
      />

      <Form.Item label="Изображение" name="coverObjectKey">
        <ImageUpload previewUrl={coverUrl} />
      </Form.Item>

      <Form.Item
        label="Заголовок (ru)"
        name="titleRu"
        rules={[{ required: true, message: "Заголовок обязателен" }]}
      >
        <Input maxLength={120} />
      </Form.Item>
      <Form.Item label="Заголовок (en)" name="titleEn">
        <Input maxLength={120} />
      </Form.Item>

      <Form.Item
        label="Текст (ru)"
        name="bodyRu"
        rules={[{ required: true, message: "Текст обязателен" }]}
      >
        <Input.TextArea rows={6} maxLength={2000} showCount />
      </Form.Item>
      <Form.Item label="Текст (en)" name="bodyEn">
        <Input.TextArea rows={6} maxLength={2000} showCount />
      </Form.Item>

    </>
  );
}

/**
 * Публикация отдельной кнопкой.
 *
 * Правка текста и решение «показать это всем» стоят по-разному: анонс
 * всплывает у каждого и второй раз уже не всплывёт. Тумблер в форме уравнивал
 * их — снять галочку случайно было так же легко, как исправить запятую.
 */
function PublishBar({ item, onDone }: { item?: Announcement; onDone: () => void }) {
  const [busy, setBusy] = useState(false);

  if (!item?.id) return null;

  const publish = async (published: boolean) => {
    setBusy(true);
    try {
      await request(`/api/v1/admin/announcements/${item.id}/publish`, {
        method: "POST",
        body: { published },
      });
      message.success(published ? "Анонс опубликован" : "Анонс снят с публикации");
      onDone();
    } catch (error) {
      message.error(error instanceof Error ? error.message : "Не удалось изменить публикацию");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Space style={{ marginBottom: 16 }} wrap>
      {item.published ? <Tag color="green">Опубликован</Tag> : <Tag>Черновик</Tag>}

      {item.publishedAt && (
        <Typography.Text type="secondary">
          Опубликован {new Date(item.publishedAt).toLocaleString("ru-RU")}
        </Typography.Text>
      )}
      {item.author?.username && (
        <Typography.Text type="secondary">· автор {item.author.username}</Typography.Text>
      )}

      <Button type={item.published ? "default" : "primary"} loading={busy} onClick={() => void publish(!item.published)}>
        {item.published ? "Снять с публикации" : "Опубликовать"}
      </Button>
    </Space>
  );
}

export function AnnouncementCreate() {
  const { formProps, saveButtonProps } = useForm<Announcement>({ resource: "announcements" });

  return (
    <Create title="Новый анонс" saveButtonProps={saveButtonProps}>
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="Анонс сохранится черновиком"
        description="Опубликовать его можно будет кнопкой на странице анонса, когда текст будет готов."
      />
      <Form {...formProps} layout="vertical">
        <Fields />
      </Form>
    </Create>
  );
}

export function AnnouncementEdit() {
  const { formProps, saveButtonProps, query } = useForm<Announcement>({ resource: "announcements" });
  const item = query?.data?.data;

  return (
    <Edit title="Анонс" saveButtonProps={saveButtonProps}>
      <PublishBar item={item} onDone={() => void query?.refetch()} />
      <Form {...formProps} layout="vertical">
        <Fields coverUrl={item?.coverImageUrl} />
      </Form>
    </Edit>
  );
}
